/**
 * What each framework is, in a few sentences.
 *
 * Each explanation ends by naming what the framework does NOT do. That last
 * sentence is the load-bearing one: a founder reading a quadrant assumes market
 * share, and a reader of an exit analysis assumes a prediction. Saying so once,
 * where they ask what they are looking at, is cheaper than a disclaimer stapled
 * to every card.
 */

import React from 'react';
import { Text, View } from 'react-native';
import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import { ANALYSIS_LABELS, type AnalysisType } from '@/lib/strategic-analysis/types';

export const FRAMEWORK_EXPLANATIONS: Record<AnalysisType, string> = {
  competition:
    'Maps the alternatives a customer could choose instead of you, on two axes selected from a fixed library because the evidence supports both. Each position is an analytical estimate drawn from cited sources — not market share, quality, or a ranking.',
  porter:
    "Assesses the five structural forces acting on your market — rivalry, new entrants, buyer power, supplier power and substitutes — against a fixed library of drivers. Each force reads Low, Moderate, High or Insufficient evidence. There is no overall score, because a market's attractiveness is not an average.",
  pestel:
    'Collects source-backed political, economic, social, technological, environmental and legal developments that apply to your jurisdictions, and states the posture of each category. Points to consider are questions, signals and robust actions — never predictions of what will happen.',
  market_validation:
    'Compares your own TAM, SAM and SOM against independent top-down and bottom-up estimates built from cited sources. Every figure is calculated in code from stored inputs, so each one can be reproduced. Where market definitions do not align, it says so instead of comparing anyway.',
  exit_strategy:
    "Compares the founder's exit thesis with evidence-supported pathways, potential strategic acquirers, comparable transactions, counterarguments, and readiness milestones. It informs judgment; it does not predict a transaction.",
};

interface FrameworkInfoModalProps {
  visible: boolean;
  onClose: () => void;
  analysisType: AnalysisType;
}

export function FrameworkInfoModal({ visible, onClose, analysisType }: FrameworkInfoModalProps) {
  const colors = useThemeColors();

  return (
    <AppModal
      visible={visible}
      onClose={onClose}
      title={ANALYSIS_LABELS[analysisType]}
      footer={
        <AppModal.Actions>
          <ModalAction label="Close" onPress={onClose} variant="neutral" />
        </AppModal.Actions>
      }
    >
      <View className="gap-4">
        <Text className="text-base leading-6" style={{ color: colors.text.primary }}>
          {FRAMEWORK_EXPLANATIONS[analysisType]}
        </Text>
        <View className="rounded-xl p-3.5" style={{ backgroundColor: colors.surface.secondary }}>
          <Text className="text-sm leading-5" style={{ color: colors.text.secondary }}>
            Every statement here links to the evidence that supports it. Where the evidence did not
            support a conclusion, the analysis says so rather than filling the gap.
          </Text>
        </View>
      </View>
    </AppModal>
  );
}
